'use client';

import { useFormTemplateKey } from '@commenda/api/form-builder/form-template-keys';
import { useFormsTemplate } from '@commenda/api/form-builder/forms-templates';
import { Input, Label, LoadingIndicator, MultiSelect } from '@commenda/kakashi';
import { useMemo } from 'react';
import { getFieldsFromTemplateDSL } from '../../utils/dsl-traversal';
import { KeySelector } from './KeySelector';

export function NestedFormEditor({
  props,
  onUpdateProps,
}: {
  props: Record<string, unknown>;
  onUpdateProps: (key: string, value: unknown) => void;
}) {
  const keyId = (props.keyId as string) || '';
  const { key } = useFormTemplateKey(keyId || null);
  const { template, isLoading: isLoadingTemplate } = useFormsTemplate(
    key?.liveFormId ?? null
  );

  const linkedFields = useMemo(() => {
    if (!template?.dsl) return [];
    return getFieldsFromTemplateDSL(template.dsl);
  }, [template?.dsl]);

  // Templates authored before multiple display fields carry a single
  // `displayFieldId`; show it selected so re-saving migrates it.
  const selectedFieldIds = useMemo(() => {
    const configured = props.displayFieldIds ?? props.displayFieldId;
    const ids = Array.isArray(configured) ? configured : [configured];
    return ids.filter(
      (id): id is string => typeof id === 'string' && id !== ''
    );
  }, [props.displayFieldIds, props.displayFieldId]);

  return (
    <div className="space-y-4">
      <h3 className="txt-small-medium text-text-secondary">Nested Form</h3>

      <div className="space-y-2">
        <Label>Linked Form Key</Label>
        <KeySelector
          value={keyId}
          onValueChange={(v) => {
            onUpdateProps('keyId', v);
            onUpdateProps('displayFieldIds', undefined);
            onUpdateProps('displayFieldId', undefined);
          }}
          placeholder="Select a template key"
        />
        <p className="txt-xs-regular text-text-tertiary">
          The form template key whose live form will be opened in the drawer
        </p>
      </div>

      {keyId && (
        <div className="space-y-2">
          <Label>Display fields</Label>
          {isLoadingTemplate ? (
            <div className="flex items-center gap-2 py-1">
              <LoadingIndicator />
              <span className="txt-xs-regular text-text-tertiary">
                Loading fields...
              </span>
            </div>
          ) : linkedFields.length > 0 ? (
            <MultiSelect
              defaultValue={selectedFieldIds}
              options={linkedFields.map((field) => ({
                value: field.fieldId,
                label: field.label,
              }))}
              onValueChange={(next) => {
                // Store in the linked form's own field order so the label
                // reads "First Last" however the boxes were ticked.
                const ordered = linkedFields
                  .map((field) => field.fieldId)
                  .filter((id) => next.includes(id));
                onUpdateProps(
                  'displayFieldIds',
                  ordered.length > 0 ? ordered : undefined
                );
                onUpdateProps('displayFieldId', undefined);
              }}
              placeholder="Auto-detect (first field)"
            />
          ) : (
            <p className="txt-xs-regular text-text-tertiary">
              No fields found in the linked form
            </p>
          )}
          <p className="txt-xs-regular text-text-tertiary">
            Field values joined with a space label each entry in the list
          </p>
        </div>
      )}

      <div className="space-y-2">
        <Label>Button Text</Label>
        <Input
          value={(props.buttonText as string) || ''}
          onChange={(e) => onUpdateProps('buttonText', e.target.value)}
          placeholder="Add"
        />
        <p className="txt-xs-regular text-text-tertiary">
          Text shown on the button that opens the nested form
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label>Minimum entries</Label>
          <Input
            type="number"
            min={0}
            value={props.min != null ? String(props.min) : ''}
            onChange={(e) => {
              const v = e.target.value;
              onUpdateProps('min', v === '' ? undefined : Number(v));
            }}
            placeholder="No minimum"
          />
        </div>
        <div className="space-y-2">
          <Label>Maximum entries</Label>
          <Input
            type="number"
            min={1}
            value={props.max != null ? String(props.max) : ''}
            onChange={(e) => {
              const v = e.target.value;
              onUpdateProps('max', v === '' ? undefined : Number(v));
            }}
            placeholder="No maximum"
          />
        </div>
      </div>
      <p className="txt-xs-regular text-text-tertiary -mt-2">
        Leave blank for no limit. Add button is disabled at max.
      </p>
    </div>
  );
}
